/**
 * TabEmptyState Component
 *
 * Generic empty state for dashboard tabs when no data matches filters.
 * Used in Active Alerts, Monitored Products, and Alert History tabs.
 * Features a decorative blob SVG backdrop for visual interest.
 * Supports both Polaris icons and custom EmptyStateIllustration variants.
 *
 * @example
 * ```tsx
 * // With Polaris icon
 * <TabEmptyState
 *   icon={NotificationIcon}
 *   title="No alerts match your filters"
 *   description="Try adjusting your search or filters"
 *   action={{ content: "Clear filters", onClick: handleClear }}
 * />
 *
 * // With custom illustration
 * <TabEmptyState
 *   illustration="no-active-alerts"
 *   title="No active alerts"
 *   description="All your inventory is looking healthy"
 * />
 * ```
 */

import { Box, BlockStack, Text, Icon, Button } from '@shopify/polaris';
import type { IconSource } from '@shopify/polaris';
import type { ButtonProps } from '@shopify/polaris';
import { EmptyStateIllustration, type EmptyStateVariant } from '../ui/EmptyStateIllustration';

/**
 * Action configuration for TabEmptyState
 */
export interface TabEmptyStateAction {
  /** Button text */
  content: string;
  /** Click handler */
  onClick: () => void;
  /** Button variant (default: undefined = secondary) */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'plain';
  /** Optional icon */
  icon?: IconSource;
  /** Loading state */
  loading?: boolean;
}

/**
 * Props for TabEmptyState component
 */
export interface TabEmptyStateProps {
  /** Icon to display (from @shopify/polaris-icons) - used when no illustration provided */
  icon?: IconSource;
  /** Custom illustration variant - takes precedence over icon */
  illustration?: EmptyStateVariant;
  /** Title text */
  title: string;
  /** Description text */
  description: string;
  /** Optional primary action button */
  action?: TabEmptyStateAction;
  /** Optional secondary action button */
  secondaryAction?: TabEmptyStateAction;
  /** Size variant for the backdrop (default: 'md') */
  backdropSize?: 'sm' | 'md' | 'lg';
}

/**
 * Renders an action button with consistent styling
 */
function ActionButton({ action }: { action: TabEmptyStateAction }) {
  return (
    <Button
      onClick={action.onClick}
      variant={action.variant}
      icon={action.icon}
      loading={action.loading}
    >
      {action.content}
    </Button>
  );
}

/**
 * Tab-level empty state component for dashboard tabs
 * Features a decorative blob SVG backdrop
 * Supports both Polaris icons and custom EmptyStateIllustration variants
 */
export function TabEmptyState({
  icon,
  illustration,
  title,
  description,
  action,
  secondaryAction,
  backdropSize = 'md',
}: TabEmptyStateProps) {
  const backdropClassName = `empty-state-backdrop empty-state-backdrop--${backdropSize}`;

  return (
    <Box paddingBlock="200">
      <div className="empty-state-enter">
        <div className={backdropClassName}>
          <BlockStack gap="400" inlineAlign="center">
            {/* Visual element: illustration takes precedence over icon */}
            {illustration ? (
              <div className="tab-empty-state-illustration">
                <EmptyStateIllustration variant={illustration} />
              </div>
            ) : icon ? (
              <div className="tab-empty-state-icon">
                <div className="tab-empty-state-icon-scale">
                  <Icon source={icon} tone="subdued" />
                </div>
              </div>
            ) : null}

            {/* Text content */}
            <BlockStack gap="200" inlineAlign="center">
              <Text as="h3" variant="headingMd" alignment="center">
                {title}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                {description}
              </Text>
            </BlockStack>

            {/* Action buttons */}
            {(action || secondaryAction) && (
              <BlockStack gap="200" inlineAlign="center">
                {action && <ActionButton action={action} />}
                {secondaryAction && <ActionButton action={secondaryAction} />}
              </BlockStack>
            )}
          </BlockStack>
        </div>
      </div>
    </Box>
  );
}
